import * as React from 'react'
import { PropsWithChildren, ReactElement } from 'react'
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'

type Props = {
    className?: string
    /** Explicit `grid-template-columns` (e.g. '2fr 1fr 1fr 2fr'). When set the row is a grid that keeps
     * everything on one line; otherwise the fields flow and wrap. */
    template?: string
}

const useStyles = makeStyles({
    row: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalM,
        alignItems: 'end',
        // Every direct child grows to fill the row and wraps to the next line when it can't fit its basis.
        // This keeps the fields fluid from the ~300px taskpane up to the wide pane without media queries.
        '> *': {
            flex: '1 1 160px',
            minWidth: 0,
        },
    },
    grid: {
        display: 'grid',
        gap: tokens.spacingHorizontalM,
        alignItems: 'end',
        // Fluent controls (Input, Combobox ~250px, DatePicker) carry an intrinsic min-width that
        // overflows a narrow grid track and spills over the next column. Let them shrink to their
        // cell. The `.fui-Field` descendant selector outranks Fluent's own single-class min-width
        // rule (equal specificity would otherwise lose on source order), so the override actually wins.
        '& > *': { minWidth: 0 },
        '& .fui-Field *': { minWidth: 0 },
    },
})

/**
 * A fluid row of form fields: they sit side by side when there's room and wrap underneath each other
 * when the pane narrows. Pass `template` to fix explicit column proportions on one line instead.
 */
export default function FieldRow(props: PropsWithChildren<Props>): ReactElement {
    const styles = useStyles()

    if (props.template) {
        return (
            <div className={mergeClasses(styles.grid, props.className)} style={{ gridTemplateColumns: props.template }}>
                {props.children}
            </div>
        )
    }

    return <div className={mergeClasses(styles.row, props.className)}>{props.children}</div>
}
